// src/crm/campaigns/CampaignDetailPage.tsx
import { useNavigate, useParams } from 'react-router-dom'
import { useAppStore } from '../../shell/appStore'
import { useCampaign } from '../hooks/useCampaigns'
import { useDeals } from '../hooks/useDeals'
import { useCreators } from '../api'
import { STAGE_LABELS } from '../pipeline/stages'
import { EstimatorPanel } from './EstimatorPanel'

export function CampaignDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const appId = useAppStore((s) => s.activeAppId)

  const { data: campaign, isLoading, isError } = useCampaign(id ?? '')
  const { data: deals = [] } = useDeals(appId)
  const { data: contacts = [] } = useCreators()

  const nameById = new Map(contacts.map((c) => [c.id, c.displayName]))
  const campaignDeals = deals.filter((d) => d.campaignId === id)
  const engaged = campaignDeals
    .filter((d) => d.stage !== 'decline_archive')
    .reduce((sum, d) => sum + (d.amount ?? 0), 0)

  if (isLoading) {
    return <div style={{ padding: '2rem 2.5rem' }}><p style={{ color: 'var(--muted)' }}>Chargement…</p></div>
  }
  if (isError || !campaign) {
    return <div style={{ padding: '2rem 2.5rem' }}><p style={{ color: 'var(--arancio)' }}>Campagne introuvable.</p></div>
  }

  return (
    <div data-testid="campaign-detail" style={{ padding: '2rem 2.5rem', maxWidth: '980px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button type="button" onClick={() => navigate('/crm/campaigns')}
          style={{ background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.375rem 0.625rem', color: 'var(--muted)', fontSize: '0.8125rem', cursor: 'pointer' }}>
          ← Campagnes
        </button>
        <h1 style={{ fontSize: '1.375rem', fontWeight: 600, color: 'var(--fg)', margin: 0, letterSpacing: '-0.025em' }}>
          {campaign.name}
        </h1>
        <span style={{ fontSize: '0.75rem', color: 'var(--muted)', border: '1px solid var(--line)', borderRadius: '5px', padding: '0.125rem 0.5rem' }}>
          {campaign.status}
        </span>
      </div>

      <section style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem', display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
        <Info label="Objectif" value={campaign.goal ?? '—'} />
        <Info label="Budget" value={campaign.budgetAmount != null ? `${campaign.budgetAmount.toFixed(2)} ${campaign.currency}` : '—'} />
        <Info label="Engagé (deals)" value={`${engaged.toFixed(2)} €`} testid="campaign-engaged" />
        <Info label="Dates" value={`${campaign.startDate ?? '—'} → ${campaign.endDate ?? '—'}`} />
      </section>

      <section data-testid="campaign-deals" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 0.75rem' }}>
          Deals ({campaignDeals.length})
        </h2>
        {campaignDeals.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', margin: 0 }}>
            Aucun deal — utilisez l'estimateur ci-dessous pour lancer l'outreach.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Contact', 'Étape', 'Montant'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--muted)', fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaignDeals.map((d) => (
                <tr key={d.id} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '0.625rem 0.75rem', color: 'var(--fg)', fontWeight: 500 }}>
                    {nameById.get(d.creatorId) ?? d.creatorId}
                  </td>
                  <td style={{ padding: '0.625rem 0.75rem', color: 'var(--muted)' }}>{STAGE_LABELS[d.stage]}</td>
                  <td style={{ padding: '0.625rem 0.75rem', color: 'var(--fg)' }}>
                    {d.amount != null ? `${d.amount.toFixed(2)} ${d.currency}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <EstimatorPanel
        campaign={campaign}
        existingCreatorIds={new Set(campaignDeals.map((d) => d.creatorId))}
      />
    </div>
  )
}

function Info({ label, value, testid }: { label: string; value: string; testid?: string }) {
  return (
    <div {...(testid ? { 'data-testid': testid } : {})}>
      <p style={{ margin: 0, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)' }}>{label}</p>
      <p style={{ margin: '0.25rem 0 0', fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)' }}>{value}</p>
    </div>
  )
}
